import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("polling");
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);
  const { refresh } = useCart();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attemptsRef.current >= 10) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-32 text-center" data-testid="checkout-success-page">
      {status === "polling" && (
        <div>
          <Loader2 className="w-10 h-10 animate-spin text-olive mx-auto" />
          <h1 className="font-serif text-4xl mt-6">Confirming your payment…</h1>
          <p className="text-ink-soft mt-3">A moment while we verify with Stripe.</p>
        </div>
      )}
      {status === "paid" && (
        <div data-testid="checkout-paid">
          <CheckCircle2 className="w-14 h-14 text-olive mx-auto" />
          <h1 className="font-serif text-5xl mt-6 tracking-tight">Order placed.</h1>
          <p className="text-ink-soft mt-4 max-w-md mx-auto">
            Thank you — your producers have been notified. You'll receive an email once your bottles ship.
          </p>
          {details?.order_id && <p className="overline mt-6" data-testid="order-id">Order {details.order_id}</p>}
          <div className="flex justify-center gap-4 mt-10">
            <Link to="/account" className="bg-olive text-bone px-8 py-4 rounded-sm hover:bg-olive-dark text-sm" data-testid="view-orders">View my orders</Link>
            <Link to="/shop" className="border border-olive text-olive px-8 py-4 rounded-sm hover:bg-boneAlt text-sm">Keep shopping</Link>
          </div>
        </div>
      )}
      {(status === "expired" || status === "error") && (
        <div data-testid="checkout-error">
          <XCircle className="w-14 h-14 text-terracotta mx-auto" />
          <h1 className="font-serif text-4xl mt-6">We couldn't confirm your payment.</h1>
          <p className="text-ink-soft mt-3">Please return to your cart and try again.</p>
          <Link to="/cart" className="inline-block mt-8 bg-olive text-bone px-8 py-4 rounded-sm text-sm">Back to cart</Link>
        </div>
      )}
    </div>
  );
}
